import Models from '@Admin/views/Models.vue';
import PointClouds from '@Admin/views/PointClouds.vue';
import GpsSettings from '@Admin/views/GpsSettings.vue';
import LevelAndArea from '@Admin/views/LevelAndArea.vue';
import Areas from '@Admin/views/Areas.vue';
import Projects from '@Admin/views/Projects.vue';
import Categories from '@Admin/views/Categories.vue';
import Stamps from '@Admin/views/Stamps.vue';
import Symbols from '@Admin/views/Symbols.vue';
import tagHarvesters from '@Admin/views/Tags.vue';
import Inspections from '@Admin/views/Inspections.vue';
import BulkImport from '@Admin/views/BulkImport.vue';
import setting from '@Admin/views/setting.vue';
import Admin from '@Admin/views/index.vue';

import permissions from '@Share/config/permissions';
import { $has } from '@/utils';

/**
 * Facility admin pages. Each declares the permission its AdminSideMenu entry checks, so a
 * pasted URL is refused by the global guard just as the menu greys the entry out. The parent
 * route adds no requirement of its own. The API stays the real boundary. Order matters: the
 * admin landing opens the first page the user may see.
 */
const adminPages = [
  {
    path: 'models',
    name: 'models',
    component: Models,
    meta: {
      title: 'Models',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.Admin3DModels.View],
    },
  },
  {
    path: 'pointCloud',
    name: 'PointClouds',
    component: PointClouds,
    meta: {
      title: 'Point Clouds',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.AdminPointClouds.Upload],
    },
  },
  {
    path: 'GPSSettings',
    name: 'GpsSettings',
    component: GpsSettings,
    meta: {
      title: 'GPS Settings',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.GPSSettings.Access],
    },
  },
  {
    path: 'LevelAndArea',
    name: 'LevelAndArea',
    component: LevelAndArea,
    meta: {
      title: 'Level And Area',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.AdminFacilityLevels.View],
    },
  },
  {
    path: 'Areas',
    name: 'Areas',
    component: Areas,
    meta: {
      title: 'Areas',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.Areas.View],
    },
  },
  {
    path: 'Projects',
    name: 'Projects',
    component: Projects,
    meta: {
      title: 'Projects',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.Admin360Images.View],
    },
  },
  {
    path: 'Categories',
    name: 'Categories',
    component: Categories,
    meta: {
      title: 'Categories',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.Categories.View],
    },
  },
  {
    path: 'markupsStamps',
    name: 'Stamps',
    component: Stamps,
    meta: {
      title: 'Stamps',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.AdminMarkupsStamps.View],
    },
  },
  {
    path: 'Symbols',
    name: 'Symbols',
    component: Symbols,
    meta: {
      title: 'Symbols',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.AdminMarkupSymbols.View],
    },
  },
  {
    path: 'tagHarvesters',
    name: 'tagHarvesters',
    component: tagHarvesters,
    meta: {
      title: 'tagHarvesters',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.TagHarvester.View],
    },
  },
  {
    path: 'Inspections',
    name: 'Inspections',
    component: Inspections,
    meta: {
      title: 'Inspections',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.InspectionTags.View],
    },
  },
  {
    path: 'setting',
    name: 'setting',
    component: setting,
    meta: {
      title: 'setting',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.GeneralSettings.View],
    },
  },
  {
    path: 'bulkImport',
    name: 'BulkImport',
    component: BulkImport,
    meta: {
      title: 'Bulk Import',
      requiresAuth: true,
      layout: 'admin-layout',
      permissions: [permissions.Tags.Masters.Update],
    },
  },
];

/**
 * Where `/:id/admin` lands: the first admin page the user may open. With none, the first page,
 * so the global guard decides as for any other page (sign-in first, then access denied).
 */
export function adminLandingRoute(to) {
  const page = adminPages.find((candidate) => $has(candidate.meta.permissions)) || adminPages[0];
  return { name: page.name, params: to.params };
}

export default [
  {
    path: '/:id/admin',
    component: Admin,
    meta: {
      requiresAuth: true,
      facilityScope: true,
    },
    children: [{ path: '', redirect: adminLandingRoute }, ...adminPages],
  },
];
